import { useCallback, useContext } from 'react';
import { PerformanceAnalyticsContext } from './PerformanceAnalyticsProvider';
import { type AppPerformanceProfilingEvent } from './';
import { FrameProfilingEventCaptureTransformer } from './frameEventTransforms';
import { fg } from '@atlaskit/platform-feature-flags';

export const usePerformanceAnalytics = (): {
	captureEvent: (event: AppPerformanceProfilingEvent) => void;
	captureUntrustedEvent: (event: unknown) => void;
} => {
	const timelineRef = useContext(PerformanceAnalyticsContext);

	// Use for out-of-frame capture in platform code.
	const captureEvent = useCallback(
		(event: AppPerformanceProfilingEvent): void => {
			if (!fg('forge-ui-performance-analytics-provider')) {
				return;
			}

			timelineRef.current!.events.push(event);
			timelineRef.current!.listeners.forEach((listener) => listener(event));
		},
		[timelineRef],
	);

	// Use for capturing events from untrusted sources (e.g. in-frame via bridge).
	const captureUntrustedEvent = useCallback(
		(event: unknown): void => {
			if (!fg('forge-ui-performance-analytics-provider')) {
				return;
			}

			const result = FrameProfilingEventCaptureTransformer.safeParse(event);
			if (!result.success) {
				// TODO: log failed validations to detect contract mismatches/tampering.
				return;
			}

			captureEvent(result.data);
		},
		[captureEvent],
	);

	return { captureEvent, captureUntrustedEvent };
};
